import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/settings")({ component: AdminSettings });

function AdminSettings() {
  const qc = useQueryClient();

  const { data: setting, isLoading } = useQuery({
    queryKey: ["app-setting", "email_verification_required"],
    queryFn: async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("value, updated_at, updated_by")
        .eq("key", "email_verification_required")
        .maybeSingle();
      return data;
    },
  });

  const required = Boolean(setting?.value);

  const toggle = useMutation({
    mutationFn: async (next: boolean) => {
      const { error } = await supabase.rpc("set_app_setting", {
        _key: "email_verification_required",
        _value: next as unknown as never,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Setting updated");
      qc.invalidateQueries({ queryKey: ["app-setting", "email_verification_required"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to update"),
  });

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="rounded-2xl border border-white/10 p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Label className="text-base">Email verification on sign-up</Label>
              {required ? (
                <Badge variant="outline">Required</Badge>
              ) : (
                <Badge variant="secondary">Disabled (instant access)</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              When enabled, new users must enter a code emailed to them before they can sign in.
              Requires your sender domain DNS to be verified — otherwise codes won't be delivered.
            </p>
          </div>
          <Switch
            checked={required}
            disabled={isLoading || toggle.isPending}
            onCheckedChange={(v) => toggle.mutate(v)}
          />
        </div>
        {setting?.updated_at && (
          <p className="text-xs text-muted-foreground">
            Last changed {new Date(setting.updated_at).toLocaleString()}
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 p-6 text-sm text-muted-foreground">
        <p className="font-medium text-foreground mb-1">How users see this</p>
        <p>The sign-up screen displays a badge reflecting this setting so users know whether they'll
        receive a verification code or be signed in immediately.</p>
      </div>
    </div>
  );
}